import type { Locale } from "../../shared/i18n";

export function parseMonth(value: string | null): Date | null {
  if (!value) return null;
  const [year, month] = value.split("-").map(Number);
  if (!year || !month) return null;
  return new Date(year, month - 1, 1);
}

export function toMonthString(date: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

export function formatMonth(value: string | null, locale: Locale, fallback = ""): string {
  const date = parseMonth(value);
  if (!date) return fallback;
  return new Intl.DateTimeFormat(locale, {
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatDate(value: string | Date | null, locale: Locale): string {
  if (!value) return "";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
}

export function formatRelative(value: string | Date, locale: Locale, now = Date.now()): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const diff = date.getTime() - now;
  const abs = Math.abs(diff);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (abs < minute) return formatter.format(0, "second");
  if (abs < hour) return formatter.format(Math.round(diff / minute), "minute");
  if (abs < day) return formatter.format(Math.round(diff / hour), "hour");
  if (abs < 7 * day) return formatter.format(Math.round(diff / day), "day");
  return formatDate(date, locale);
}

export function formatBytes(bytes: number, locale: Locale): string {
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  const number = new Intl.NumberFormat(locale, {
    maximumFractionDigits: index === 0 ? 0 : 1,
  }).format(value);
  return `${number} ${units[index]}`;
}

export function formatMoney(amount: number, currency: string, locale: Locale): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}
